import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowUpRight, FileText } from 'lucide-react';
import { api } from '../lib/api';
import SEOHead from '../components/common/SEOHead';

/**
 * Home Value Report — trust-first gated lead capture.
 * Delivered manually within 24 hours after human review of comparable sales.
 */
export default function HomeValueReport() {
    const [state, setState] = useState('form'); // 'form' | 'sending' | 'done' | 'error'
    const [err, setErr] = useState('');
    const [form, setForm] = useState({
        name: '', email: '', phone: '',
        address: '', city: '', postal_code: '',
        property_type: 'detached',
        bedrooms: '', bathrooms: '', year_built: '', approx_sqft: '',
        condition: 'good',
        timeline: '3-6-months',
        notes: '',
    });

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    async function submit(e) {
        e.preventDefault();
        setState('sending');
        setErr('');
        try {
            const payload = { ...form };
            ['bedrooms', 'bathrooms', 'year_built', 'approx_sqft'].forEach((k) => {
                if (payload[k] === '' || payload[k] == null) delete payload[k];
                else payload[k] = Number(payload[k]);
            });
            if (!payload.postal_code) delete payload.postal_code;
            if (!payload.notes) delete payload.notes;
            await api.post('/sellers/home-value-request', payload);
            setState('done');
        } catch (e2) {
            setErr(e2?.response?.data?.detail || 'Something went wrong. Please try again.');
            setState('error');
        }
    }

    if (state === 'done') return <DoneState email={form.email} />;

    return (
        <div className="bg-white min-h-screen">
            <SEOHead
                title="Home Value Report — Southern Ontario"
                description="A defensible pricing document for your home, prepared within 24 hours from comparable sales and current active listings."
                path="/sellers/home-value-report"
            />

            <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-24 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
                {/* LEFT — editorial */}
                <div className="lg:col-span-6">
                    <div className="flex items-center gap-3 text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data">
                        <span className="inline-block w-6 h-px bg-neutral-400" />
                        The Home Value Report · Southern Ontario
                    </div>
                    <h1 className="font-editorial mt-6 leading-[1.05] text-black" style={{ fontSize: 'clamp(40px, 6vw, 76px)', letterSpacing: '-0.03em' }} data-testid="hvr-title">
                        A defensible number.
                        <br />
                        <span className="italic text-neutral-500">Not an opinion.</span>
                    </h1>
                    <p className="mt-6 font-editorial text-neutral-700" style={{ fontSize: '19px', lineHeight: 1.55 }}>
                        Every home value report is prepared by hand. We pull the last twelve
                        comparable sales on your block, review current active listings within
                        two kilometres, and layer in the days-on-market trend. Delivered as a
                        one-page PDF within 24 hours.
                    </p>
                    <ul className="mt-10 space-y-4 text-[14.5px] text-neutral-800">
                        {[
                            'Twelve comparable sales, adjusted for renovations and lot',
                            'Current active competition within 2 km',
                            'Days-on-market and sale-to-list trend for your segment',
                            'A recommended list price with a range, not a single number',
                            'The reasoning, in plain language, that you can defend',
                        ].map((l, i) => (
                            <li key={i} className="flex gap-3">
                                <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#2F6B47] shrink-0" />
                                <span>{l}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-10 text-[12.5px] text-neutral-500 leading-relaxed border-t border-neutral-200 pt-6 max-w-md">
                        No listing agreement discussed on the report. This is an analyst
                        document, not a pitch.
                    </div>
                </div>

                {/* RIGHT — form */}
                <div className="lg:col-span-6">
                    <motion.form
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        onSubmit={submit}
                        className="border border-neutral-300 bg-white p-6 lg:p-8"
                       
                        data-testid="hvr-form"
                    >
                        <div className="flex items-center gap-2 text-[10.5px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data">
                            <FileText className="w-3 h-3" /> Request the report
                        </div>
                        <div className="font-editorial mt-3 leading-tight" style={{ fontSize: '26px', letterSpacing: '-0.01em' }}>
                            Ten fields. Ninety seconds.
                        </div>

                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Full name" required>
                                <input required data-testid="hvr-name" value={form.name} onChange={set('name')} className={inputCls} />
                            </Field>
                            <Field label="Email" required>
                                <input required type="email" data-testid="hvr-email" value={form.email} onChange={set('email')} className={inputCls} />
                            </Field>
                            <Field label="Phone" required>
                                <input required data-testid="hvr-phone" value={form.phone} onChange={set('phone')} className={inputCls} />
                            </Field>
                            <Field label="Postal code">
                                <input data-testid="hvr-postal" value={form.postal_code} onChange={set('postal_code')} className={inputCls} placeholder="L8N 1A1" />
                            </Field>
                            <Field label="Street address" required className="sm:col-span-2">
                                <input required data-testid="hvr-address" value={form.address} onChange={set('address')} className={inputCls} />
                            </Field>
                            <Field label="City" required>
                                <input required data-testid="hvr-city" value={form.city} onChange={set('city')} className={inputCls} placeholder="Hamilton, Oakville…" />
                            </Field>
                            <Field label="Property type" required>
                                <select required data-testid="hvr-type" value={form.property_type} onChange={set('property_type')} className={inputCls}>
                                    <option value="detached">Detached</option>
                                    <option value="semi">Semi-detached</option>
                                    <option value="townhouse">Townhouse</option>
                                    <option value="condo">Condo</option>
                                    <option value="other">Other</option>
                                </select>
                            </Field>
                            <Field label="Bedrooms">
                                <input type="number" min="0" max="20" data-testid="hvr-beds" value={form.bedrooms} onChange={set('bedrooms')} className={inputCls} />
                            </Field>
                            <Field label="Bathrooms">
                                <input type="number" step="0.5" min="0" max="20" data-testid="hvr-baths" value={form.bathrooms} onChange={set('bathrooms')} className={inputCls} />
                            </Field>
                            <Field label="Year built">
                                <input type="number" min="1800" max="2030" data-testid="hvr-year" value={form.year_built} onChange={set('year_built')} className={inputCls} />
                            </Field>
                            <Field label="Approx. sq ft">
                                <input type="number" min="200" max="20000" data-testid="hvr-sqft" value={form.approx_sqft} onChange={set('approx_sqft')} className={inputCls} />
                            </Field>
                            <Field label="Current condition" required>
                                <select required data-testid="hvr-condition" value={form.condition} onChange={set('condition')} className={inputCls}>
                                    <option value="excellent">Excellent — recently renovated</option>
                                    <option value="good">Good — well-maintained</option>
                                    <option value="fair">Fair — dated but functional</option>
                                    <option value="needs-work">Needs work</option>
                                </select>
                            </Field>
                            <Field label="Timeline" required>
                                <select required data-testid="hvr-timeline" value={form.timeline} onChange={set('timeline')} className={inputCls}>
                                    <option value="0-3-months">0–3 months</option>
                                    <option value="3-6-months">3–6 months</option>
                                    <option value="6-12-months">6–12 months</option>
                                    <option value="12-plus-months">12+ months</option>
                                    <option value="just-curious">Just curious</option>
                                </select>
                            </Field>
                            <Field label="Anything specific we should know?" className="sm:col-span-2">
                                <textarea rows={3} data-testid="hvr-notes" value={form.notes} onChange={set('notes')} className={inputCls} placeholder="Recent renovations, unusual features, upcoming decisions…" />
                            </Field>
                        </div>

                        {err && <div className="mt-4 text-[13px] text-red-600" data-testid="hvr-error">{err}</div>}

                        <button
                            type="submit"
                            disabled={state === 'sending'}
                            data-testid="hvr-submit"
                            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand-navy text-white px-6 py-4 text-[14px] font-medium hover:bg-neutral-800 disabled:opacity-60"
                            style={{ letterSpacing: '0.02em' }}
                        >
                            {state === 'sending' ? 'Sending…' : (<>Request the report <ArrowUpRight className="w-4 h-4" /></>)}
                        </button>
                        <div className="mt-4 text-[11.5px] text-neutral-500 leading-relaxed">
                            By submitting, you agree to receive the report and occasional follow-up
                            emails. Unsubscribe at any time. We never share your address.
                        </div>
                    </motion.form>
                </div>
            </div>
        </div>
    );
}

const inputCls = 'w-full border border-neutral-300 bg-white px-3 py-2.5 text-[14px] font-editorial focus:outline-none focus:border-black transition-colors';

function Field({ label, required, className, children }) {
    return (
        <label className={`block ${className || ''}`}>
            <div className="text-[10.5px] tracking-[0.2em] uppercase text-neutral-500 font-mono-data mb-1.5">
                {label}{required && ' *'}
            </div>
            {children}
        </label>
    );
}

function DoneState({ email }) {
    return (
        <div className="bg-white min-h-screen">
            <div className="max-w-[820px] mx-auto px-6 lg:px-10 pt-32 pb-24">
                <div className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data flex items-center gap-3">
                    <span className="inline-block w-6 h-px bg-neutral-400" />
                    Request received
                </div>
                <h1 className="font-editorial mt-6 leading-[1.05] text-black" data-testid="hvr-done-title" style={{ fontSize: 'clamp(36px, 5.5vw, 64px)', letterSpacing: '-0.03em' }}>
                    Thank you.
                    <br />
                    <span className="italic text-neutral-500">Your report is being prepared.</span>
                </h1>
                <p className="mt-6 font-editorial text-neutral-700 max-w-2xl" style={{ fontSize: '18px', lineHeight: 1.55 }}>
                    Request received for <span className="text-black">{email}</span>. Sam prepares each report personally within 24 hours.
                    Every report is reviewed by a human analyst before it goes out — no
                    automated valuation and no bulk template.
                </p>
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-0 border-l border-t border-neutral-200 max-w-2xl">
                    <Link to="/seller-centre" className="border-r border-b border-neutral-200 p-6 hover:bg-brand-navy hover:text-white transition-colors group">
                        <div className="overline">While you wait</div>
                        <div className="font-editorial mt-2 text-[20px] group-hover:italic">Read the twelve pillars</div>
                        <div className="mt-3 text-[12px] flex items-center gap-1">Open <ArrowUpRight className="w-3 h-3" /></div>
                    </Link>
                    <Link to="/sellers/consultation" className="border-r border-b border-neutral-200 p-6 hover:bg-brand-navy hover:text-white transition-colors group">
                        <div className="overline">Ready sooner</div>
                        <div className="font-editorial mt-2 text-[20px] group-hover:italic">Book a listing consultation</div>
                        <div className="mt-3 text-[12px] flex items-center gap-1">Book <ArrowUpRight className="w-3 h-3" /></div>
                    </Link>
                </div>
            </div>
        </div>
    );
}
